import React, { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchBar } from './components/SearchBar';
import { ComparisonResult } from './components/ComparisonResult';
import { Loader } from './components/Loader';
import { ErrorMessage } from './components/ErrorMessage';
import { getLaptopComparison } from './services/geminiService';
import { createBlogPostFromSearch } from './services/blogService';
import type { LaptopComparison, GroundingChunk } from './types';
import { SourcePill } from './components/SourcePill';
import { HistoryPanel } from './components/HistoryPanel';
import { QuickOptionsCards } from './components/QuickOptionsCards';
import { GuidedFinder } from './components/GuidedFinder';
import ComparisonPage from './components/ComparisonPage';
import { CompareLaptopsModal } from './components/CompareLaptopsModal';
import { Header } from './components/layout/Header';
import { Footer } from './components/layout/Footer';
import { AboutPage } from './components/AboutPage';
import { BlogPage } from './components/BlogPage';
import { BlogDetailPage } from './components/BlogDetailPage';
import { TermsPage } from './components/TermsPage';
import { PrivacyPage } from './components/PrivacyPage';
import { CookiePage } from './components/CookiePage';
import { FeaturedBlogSection } from './components/FeaturedBlogSection';
import { TipsSection } from './components/TipsSection';
import { STORAGE_KEYS, APP_CONFIG } from './constants';

const App: React.FC = () => {
  const [query, setQuery] = useState<string>('');
  const [comparisonResult, setComparisonResult] = useState<LaptopComparison[] | null>(null);
  const [sources, setSources] = useState<GroundingChunk[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<string[]>([]);
  const [isHistoryVisible, setIsHistoryVisible] = useState<boolean>(false);
  const [showGuidedFinder, setShowGuidedFinder] = useState<boolean>(false);
  const [showCompareModal, setShowCompareModal] = useState<boolean>(false);
  const [showCompareMode, setShowCompareMode] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<'home' | 'about' | 'blog' | 'blog-detail' | 'terms' | 'privacy' | 'cookies'>('home');
  const [blogSlug, setBlogSlug] = useState<string>('');

  useEffect(() => {
    try {
      const storedHistory = localStorage.getItem(STORAGE_KEYS.searchHistory);
      if (storedHistory) {
        setHistory(JSON.parse(storedHistory));
      }
    } catch (error) {
      console.error("Failed to parse history from localStorage", error);
      localStorage.removeItem(STORAGE_KEYS.searchHistory);
    }
  }, []);

  const updateHistory = (newQuery: string) => {
    const updatedHistory = [
      newQuery,
      ...history.filter(h => h.toLowerCase() !== newQuery.toLowerCase())
    ].slice(0, APP_CONFIG.maxHistoryItems);
    setHistory(updatedHistory);
    localStorage.setItem(STORAGE_KEYS.searchHistory, JSON.stringify(updatedHistory));
  };

  const handleSearch = useCallback(async (searchQuery?: string) => {
    const currentQuery = searchQuery || query;
    if (!currentQuery.trim()) {
      setError('Please enter a query to start the comparison.');
      return;
    }
    
    setIsHistoryVisible(false);
    setIsLoading(true);
    setError(null);
    setComparisonResult(null);
    setSources([]);

    try {
      const result = await getLaptopComparison(currentQuery);
      setComparisonResult(result.comparisons);
      setSources(result.sources);
      updateHistory(currentQuery);
      
      // Create blog post in the background (non-blocking)
      if (result.comparisons.length > 0) {
        createBlogPostFromSearch(currentQuery, result.comparisons).catch(err => {
          console.error('Failed to create blog post:', err);
          // Don't show error to user - this is a background operation
        });
      }
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : 'An unknown error occurred.');
    } finally {
      setIsLoading(false);
    }
  }, [query, history]);
  
  const handleHistorySearch = useCallback((historyQuery: string) => {
    setQuery(historyQuery);
    handleSearch(historyQuery);
  }, [handleSearch]);

  const handleClearHistory = useCallback(() => {
    setHistory([]);
    localStorage.removeItem(STORAGE_KEYS.searchHistory);
    setIsHistoryVisible(false);
  }, []);

  const handleGuidedFinderSearch = useCallback((generatedQuery: string) => {
    setQuery(generatedQuery);
    setShowGuidedFinder(false);
    handleSearch(generatedQuery);
  }, [handleSearch]);

  const handleCompareLaptopsClick = useCallback(() => {
    setShowCompareModal(true);
  }, []);

  const handleCompareSubmit = useCallback(async (laptops: string[]) => {
    const compareQuery = `Compare ${laptops.join(' vs ')}`;
    setShowCompareModal(false);
    setIsLoading(true);
    setError(null);
    setComparisonResult(null);
    setSources([]);

    try {
      const result = await getLaptopComparison(compareQuery);
      setComparisonResult(result.comparisons);
      setSources(result.sources);
      updateHistory(compareQuery);
      setShowCompareMode(true);
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : 'An unknown error occurred.');
    } finally {
      setIsLoading(false);
    }
  }, [history]);

  const handleBackFromCompare = useCallback(() => {
    setShowCompareMode(false);
  }, []);

  const handleNavigate = useCallback((page: 'home' | 'about' | 'blog' | 'blog-detail' | 'terms' | 'privacy' | 'cookies', slug?: string) => {
    setCurrentPage(page);
    if (page === 'blog-detail' && slug) {
      setBlogSlug(slug);
      // Update URL hash for SEO
      window.location.hash = `blog/${slug}`;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleBackToBlog = useCallback(() => {
    setCurrentPage('blog');
    setBlogSlug('');
    window.location.hash = 'blog';
  }, []);

  // Handle initial URL hash routing
  useEffect(() => {
    const hash = window.location.hash.slice(1); // Remove #
    if (hash.startsWith('blog/')) {
      const slug = hash.replace('blog/', '');
      if (slug && slug !== 'blog') {
        setBlogSlug(slug);
        setCurrentPage('blog-detail');
      }
    } else if (hash === 'blog') {
      setCurrentPage('blog');
    }
  }, []);

  if (currentPage === 'blog-detail' && blogSlug) {
    return (
      <>
        <Header onNavigate={handleNavigate} currentPage={currentPage} />
        <BlogDetailPage slug={blogSlug} onBack={handleBackToBlog} />
        <Footer onNavigate={handleNavigate} />
      </>
    );
  }

  if (currentPage === 'about') {
    return (
      <>
        <Header onNavigate={handleNavigate} currentPage={currentPage} />
        <AboutPage />
        <Footer onNavigate={handleNavigate} />
      </>
    );
  }

  if (currentPage === 'blog') {
    return (
      <>
        <Header onNavigate={handleNavigate} currentPage={currentPage} />
        <BlogPage onNavigate={handleNavigate} />
        <Footer onNavigate={handleNavigate} />
      </>
    );
  }

  if (currentPage === 'terms') {
    return (
      <>
        <Header onNavigate={handleNavigate} currentPage={currentPage} />
        <TermsPage />
        <Footer onNavigate={handleNavigate} />
      </>
    );
  }

  if (currentPage === 'privacy') {
    return (
      <>
        <Header onNavigate={handleNavigate} currentPage={currentPage} />
        <PrivacyPage />
        <Footer onNavigate={handleNavigate} />
      </>
    );
  }

  if (currentPage === 'cookies') {
    return (
      <>
        <Header onNavigate={handleNavigate} currentPage={currentPage} />
        <CookiePage />
        <Footer onNavigate={handleNavigate} />
      </>
    );
  }

  if (showGuidedFinder) {
    return (
      <>
        <Header onNavigate={handleNavigate} currentPage={currentPage} />
        <div className="min-h-screen bg-[var(--surface-1)] text-[var(--text-2)] font-sans">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-16">
            <header className="text-center mb-10 sm:mb-12">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[var(--text-1)] font-serif">
                CompareLens
              </h1>
              <p className="text-base sm:text-lg text-[var(--text-2)] mt-4 max-w-2xl mx-auto">
                Let us guide you to your perfect laptop
              </p>
            </header>
            <main>
              <GuidedFinder
                onBack={() => setShowGuidedFinder(false)}
                onSearch={handleGuidedFinderSearch}
              />
            </main>
          </div>
        </div>
      </>
    );
  }

  if (showCompareMode && comparisonResult && comparisonResult.length > 1) {
    return (
      <>
        <Header onNavigate={handleNavigate} currentPage={currentPage} />
        <div className="min-h-screen bg-[var(--surface-1)] text-[var(--text-2)] font-sans">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-16">
            <header className="text-center mb-10 sm:mb-12">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[var(--text-1)] font-serif">
                CompareLens
              </h1>
              <p className="text-base sm:text-lg text-[var(--text-2)] mt-4 max-w-2xl mx-auto">
                Your personal AI expert for instant, data-driven laptop recommendations.
              </p>
            </header>
            <main>
              <ComparisonPage
                laptops={comparisonResult}
                onBack={handleBackFromCompare}
              />
            </main>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header onNavigate={handleNavigate} currentPage={currentPage} />
      <div className="min-h-screen bg-gradient-to-b from-[var(--surface-1)] via-[var(--surface-2)] to-[var(--surface-1)] text-[var(--text-2)] font-sans">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-16">
          <motion.header
            className="text-center mb-10 sm:mb-12"
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          >
            <motion.h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[var(--text-1)] font-serif mb-4"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              CompareLens
            </motion.h1>
            <motion.p
              className="text-base sm:text-lg text-[var(--text-2)] mt-4 max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              Your personal AI expert for instant, data-driven laptop recommendations.
            </motion.p>
          </motion.header>

          {/* Tips Section */}
          <TipsSection hide={isLoading || !!comparisonResult || !!error} />

        <main>
          <AnimatePresence mode="wait">
            {showCompareModal && (
              <CompareLaptopsModal
                onClose={() => setShowCompareModal(false)}
                onCompare={handleCompareSubmit}
                isLoading={isLoading}
              />
            )}
          </AnimatePresence>

          <motion.div
            className="relative max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <SearchBar
              query={query}
              setQuery={setQuery}
              onSearch={() => handleSearch()}
              isLoading={isLoading}
              onToggleHistory={() => setIsHistoryVisible(prev => !prev)}
            />
            <HistoryPanel
              isVisible={isHistoryVisible}
              history={history}
              onSelect={handleHistorySearch}
              onClear={handleClearHistory}
              onClose={() => setIsHistoryVisible(false)}
            />
          </motion.div>

          <AnimatePresence mode="wait">
            {!isLoading && !comparisonResult && !error && (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -30 }}
                transition={{ duration: 0.5 }}
              >
                <QuickOptionsCards
                  onGuidedFinderClick={() => setShowGuidedFinder(true)}
                  onCompareLaptopsClick={handleCompareLaptopsClick}
                />
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence mode="wait">
            {error && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.3 }}
              >
                <ErrorMessage message={error} />
              </motion.div>
            )}
          </AnimatePresence>

          {isLoading && <Loader />}

          <AnimatePresence mode="wait">
            {comparisonResult && (
              <motion.div
                className="mt-12 sm:mt-16"
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -40 }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              >
                <ComparisonResult
                  results={comparisonResult}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {!isLoading && !comparisonResult && !error && (
            <>
             <motion.div
               className="mt-20 sm:mt-28 max-w-4xl mx-auto"
               initial={{ opacity: 0, y: 40 }}
               animate={{ opacity: 1, y: 0 }}
               transition={{ duration: 0.6, delay: 0.8 }}
             >
              <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[var(--surface-2)] via-[var(--surface-2)] to-[var(--surface-3)] border border-[var(--surface-4)] p-8 sm:p-12 shadow-xl hover:shadow-2xl transition-shadow duration-300">
                <div className="absolute top-0 right-0 w-64 h-64 bg-[var(--brand)]/5 rounded-full blur-3xl animate-pulse"></div>
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-[var(--accent)]/5 rounded-full blur-3xl animate-pulse delay-75"></div>

                <div className="relative text-center">
                  <motion.div
                    className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[var(--brand)]/10 to-[var(--accent)]/10 rounded-full mb-6"
                    whileHover={{ scale: 1.05 }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-[var(--brand)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    <span className="text-xs font-semibold bg-gradient-to-r from-[var(--brand)] to-[var(--accent)] bg-clip-text text-transparent uppercase tracking-wider">AI-Powered Intelligence</span>
                  </motion.div>

                  <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[var(--text-1)] font-serif mb-4 leading-tight">
                    Discover Your Ideal Match
                  </h3>

                  <p className="text-[var(--text-2)] text-base sm:text-lg max-w-2xl mx-auto leading-relaxed mb-8">
                    Whether you're a creator, gamer, or professional, our AI analyzes thousands of laptops to find your perfect match in seconds.
                  </p>

                  <div className="flex flex-wrap items-center justify-center gap-3 text-sm text-[var(--text-3)]">
                    {['Real-time Data', 'Unbiased Analysis', 'Instant Results'].map((feature, index) => (
                      <motion.div
                        key={feature}
                        className="flex items-center gap-2 bg-[var(--surface-3)] px-4 py-2 rounded-lg hover:bg-[var(--brand-lighter)] hover:text-[var(--brand)] transition-colors duration-200"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: 1 + index * 0.1 }}
                        whileHover={{ scale: 1.05 }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-[var(--success)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span>{feature}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
            <FeaturedBlogSection onNavigate={handleNavigate} />
            </>
          )}
        </main>
        </div>
      </div>
      {!isLoading && !comparisonResult && !error && <Footer onNavigate={handleNavigate} />}
    </>
  );
};

export default App;